import type { HealthParameterKey } from '@/domain/health';

/** Health detail pages (single-route or tabbed section). */
export type HealthDetailPageId =
    | 'maturation'
    | 'finStatus'
    | 'emaciation'
    | 'cataract'
    | 'scaleLoss'
    | 'skinBleeding'
    | 'spine'
    | 'jaw'
    | 'gillLid'
    | 'eyeDamage'
    | 'bodyWounds'
    | 'snoutWounds';

export type HealthDetailRouteLabelKey = HealthDetailPageConfig['routeLabelKey'];

export interface HealthDetailPageConfig {
    pageId: HealthDetailPageId;
    /** Route segment under `/health`. */
    routePath: string;
    /** Key in `HealthOverviewData.summary.parameters`. */
    healthParameterKey: HealthParameterKey;
    /** Image indicator key in `laksvelImages` (`*_score`). */
    laksvelImageIndicator: string;
    /** i18n route label under `healthModule.routes`. */
    routeLabelKey:
        | 'maturation'
        | 'finStatus'
        | 'emaciation'
        | 'cataract'
        | 'scaleLoss'
        | 'skinBleeding'
        | 'spine'
        | 'jaw'
        | 'gillLid'
        | 'eyeDamage'
        | 'bodyWounds'
        | 'snoutWounds';
    /** i18n namespace suffix under `healthModule.detail.<pageId>`. */
    detailNamespace: HealthDetailPageId;
}

export const HEALTH_DETAIL_PAGE_CONFIG: Record<HealthDetailPageId, HealthDetailPageConfig> = {
    maturation: {
        pageId: 'maturation',
        routePath: 'maturation',
        healthParameterKey: 'maturity',
        laksvelImageIndicator: 'maturity_score',
        routeLabelKey: 'maturation',
        detailNamespace: 'maturation',
    },
    finStatus: {
        pageId: 'finStatus',
        routePath: 'fin-status',
        healthParameterKey: 'finDamage',
        laksvelImageIndicator: 'fin_damage_score',
        routeLabelKey: 'finStatus',
        detailNamespace: 'finStatus',
    },
    emaciation: {
        pageId: 'emaciation',
        routePath: 'emaciation',
        healthParameterKey: 'emaciation',
        laksvelImageIndicator: 'emaciation_score',
        routeLabelKey: 'emaciation',
        detailNamespace: 'emaciation',
    },
    cataract: {
        pageId: 'cataract',
        routePath: 'cataract',
        healthParameterKey: 'cataract',
        laksvelImageIndicator: 'cataract_score',
        routeLabelKey: 'cataract',
        detailNamespace: 'cataract',
    },
    scaleLoss: {
        pageId: 'scaleLoss',
        routePath: 'scale-loss',
        healthParameterKey: 'scaleLoss',
        laksvelImageIndicator: 'scale_loss_score',
        routeLabelKey: 'scaleLoss',
        detailNamespace: 'scaleLoss',
    },
    skinBleeding: {
        pageId: 'skinBleeding',
        routePath: 'skin-bleeding',
        healthParameterKey: 'skinBleeding',
        laksvelImageIndicator: 'skin_bleeding_score',
        routeLabelKey: 'skinBleeding',
        detailNamespace: 'skinBleeding',
    },
    spine: {
        pageId: 'spine',
        routePath: 'deformities/spine',
        healthParameterKey: 'deformity',
        laksvelImageIndicator: 'deformity_score',
        routeLabelKey: 'spine',
        detailNamespace: 'spine',
    },
    jaw: {
        pageId: 'jaw',
        routePath: 'deformities/jaw',
        healthParameterKey: 'deformedJaw',
        laksvelImageIndicator: 'deformed_jaw_score',
        routeLabelKey: 'jaw',
        detailNamespace: 'jaw',
    },
    gillLid: {
        pageId: 'gillLid',
        routePath: 'deformities/gill-lid',
        healthParameterKey: 'gillLid',
        laksvelImageIndicator: 'gill_lid_score',
        routeLabelKey: 'gillLid',
        detailNamespace: 'gillLid',
    },
    eyeDamage: {
        pageId: 'eyeDamage',
        routePath: 'eye-damage',
        healthParameterKey: 'eyeDamage',
        laksvelImageIndicator: 'eye_damage_score',
        routeLabelKey: 'eyeDamage',
        detailNamespace: 'eyeDamage',
    },
    bodyWounds: {
        pageId: 'bodyWounds',
        routePath: 'body-wounds/laksvel',
        healthParameterKey: 'bodyWounds',
        laksvelImageIndicator: 'body_wounds_score',
        routeLabelKey: 'bodyWounds',
        detailNamespace: 'bodyWounds',
    },
    snoutWounds: {
        pageId: 'snoutWounds',
        routePath: 'snout-wounds',
        healthParameterKey: 'snoutWounds',
        laksvelImageIndicator: 'snout_wounds_score',
        routeLabelKey: 'snoutWounds',
        detailNamespace: 'snoutWounds',
    },
};

export function getHealthDetailPageConfig(pageId: HealthDetailPageId): HealthDetailPageConfig {
    return HEALTH_DETAIL_PAGE_CONFIG[pageId];
}
